import { useQuery } from '@apollo/client';
import { ChevronDown, ChevronUp, Plus, Send, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PAYMENT_METHODS_QUERY } from './cashier-pos.gql';

import { centsToMajorString, formatCentsString, majorStringToCents } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface PaymentMethod {
  id: string;
  code: string;
  name: string;
  requires_reference: boolean;
  affects_cash_count: boolean;
}

interface PaymentMethodsResponse {
  paymentMethods: { items: PaymentMethod[] };
}

export interface PaymentDraftValue {
  payment_method_id: string;
  amount_cents: string;
  reference_number: string | null;
  allow_change: boolean;
  method_code: string;
  method_name: string;
}

type OpenAccountKindSelection = 'normal' | 'hospitalization_1' | 'hospitalization_2' | null;

interface PaymentPanelProps {
  order_id: string;
  total_cents: string;
  subtotal_cents: string;
  discount_cents: string;
  tax_cents: string;
  on_charge: (
    drafts: PaymentDraftValue[],
    options: { open_account_kind: OpenAccountKindSelection },
  ) => void | Promise<void>;
  on_apply_discount: (input: {
    discount_cents: string;
    reason: string | null;
    otp_plaintext: string;
  }) => void | Promise<void>;
  charging: boolean;
  applying_discount: boolean;
  on_cancel: () => void;
  disabled?: boolean;
  disabled_reason?: string | null;
  open_account_available?: boolean;
}

export function PaymentPanel({
  order_id,
  total_cents,
  subtotal_cents,
  discount_cents,
  tax_cents,
  on_charge,
  on_apply_discount,
  charging,
  applying_discount,
  on_cancel,
  disabled = false,
  disabled_reason = null,
  open_account_available = false,
}: PaymentPanelProps): JSX.Element {
  const { t } = useTranslation();
  const { data: methods_data } = useQuery<PaymentMethodsResponse>(PAYMENT_METHODS_QUERY, {
    variables: { offset: 0, limit: 50 },
    fetchPolicy: 'cache-first',
  });
  const methods = useMemo(
    () =>
      (methods_data?.paymentMethods.items ?? [])
        .slice()
        .sort((a, b) => a.code.localeCompare(b.code)),
    [methods_data],
  );

  const [drafts, setDrafts] = useState<PaymentDraftValue[]>([]);
  const [open_account_kind, setOpenAccountKind] = useState<OpenAccountKindSelection>(null);
  // Confirmation that the patient can't cover the Hosp.1 minimum — required for Hosp.2.
  const [hosp2_confirmed, setHosp2Confirmed] = useState(false);
  const chooseOpenAccountKind = (kind: OpenAccountKindSelection): void => {
    setOpenAccountKind(kind);
    setHosp2Confirmed(false);
  };
  const [discount_amount_major, setDiscountAmountMajor] = useState(
    centsToMajorString(discount_cents),
  );
  const [discount_reason, setDiscountReason] = useState('');
  const [discount_otp, setDiscountOtp] = useState('');
  const [discount_section_open, setDiscountSectionOpen] = useState(false);
  const previous_total_cents_ref = useRef(total_cents);

  useEffect(() => {
    if (drafts.length === 0 && methods.length > 0) {
      const cash = methods.find((m) => m.code === 'cash') ?? methods[0];
      if (cash) {
        setDrafts([
          {
            payment_method_id: cash.id,
            amount_cents: total_cents,
            reference_number: null,
            allow_change: cash.code === 'cash',
            method_code: cash.code,
            method_name: cash.name,
          },
        ]);
      }
    }
  }, [methods, drafts.length, total_cents]);

  useEffect(() => {
    setDiscountAmountMajor(centsToMajorString(discount_cents));
    setDiscountReason('');
    setDiscountOtp('');
    setDiscountSectionOpen(false);
  }, [order_id, discount_cents]);

  useEffect(() => {
    const previous_total_cents = previous_total_cents_ref.current;
    if (previous_total_cents === total_cents) return;

    setDrafts((prev) => {
      if (prev.length !== 1) return prev;
      const [only_draft] = prev;
      if (!only_draft || only_draft.amount_cents !== previous_total_cents) return prev;
      return [{ ...only_draft, amount_cents: total_cents }];
    });

    previous_total_cents_ref.current = total_cents;
  }, [total_cents]);

  const total_paid_cents = drafts.reduce<bigint>(
    (acc, d) => acc + (safeBigInt(d.amount_cents) ?? 0n),
    0n,
  );
  const total_target = safeBigInt(total_cents) ?? 0n;
  const subtotal_target = safeBigInt(subtotal_cents) ?? 0n;
  const discount_target = safeBigInt(discount_cents) ?? 0n;
  const tax_target = safeBigInt(tax_cents) ?? 0n;
  const max_discount_cents = (subtotal_target * 49n) / 100n;
  const discount_input_cents = parseMoneyInput(discount_amount_major);
  const remaining_cents = total_target - total_paid_cents;
  // Hosp.1 overpay is intentional — the surplus is booked as account credit (no change,
  // no "overpaid" block). The backend skips the change/split branch for hospitalization_1.
  const hosp1_overpay = open_account_kind === 'hospitalization_1' && remaining_cents < 0n;
  const has_change = remaining_cents < 0n && drafts.some((d) => d.allow_change) && !hosp1_overpay;
  const is_balanced = remaining_cents === 0n;
  const is_overpaid_no_change = remaining_cents < 0n && !has_change && !hosp1_overpay;
  const hosp1_credit_cents = hosp1_overpay ? -remaining_cents : 0n;
  const is_underpaid = remaining_cents > 0n;
  const change_cents = remaining_cents < 0n ? -remaining_cents : 0n;
  const minimum_hospitalization_payment_cents = (total_target + 1n) / 2n;
  const hospitalization_meets_minimum =
    total_paid_cents >= minimum_hospitalization_payment_cents && total_target > 0n;

  const missing_reference = drafts.some(
    (d) => methodFor(d, methods)?.requires_reference && !d.reference_number?.trim(),
  );

  const allow_open_account = open_account_kind !== null && open_account_available;
  const final_can_submit =
    !disabled &&
    !applying_discount &&
    drafts.every((d) => {
      const amount = safeBigInt(d.amount_cents);
      return amount !== null && amount >= 0n;
    }) &&
    drafts.some((d) => (safeBigInt(d.amount_cents) ?? 0n) > 0n || allow_open_account) &&
    !missing_reference &&
    (open_account_kind === 'hospitalization_1'
      ? hospitalization_meets_minimum
      : !is_underpaid || allow_open_account) &&
    (open_account_kind === 'hospitalization_1' ? true : !is_overpaid_no_change) &&
    (open_account_kind === 'hospitalization_2' ? hosp2_confirmed : true);

  const can_apply_discount =
    !disabled &&
    !charging &&
    !applying_discount &&
    discount_input_cents !== null &&
    discount_input_cents >= 0n &&
    discount_input_cents <= max_discount_cents &&
    discount_otp.trim().length > 0;

  const onClickAddDraft = (): void => {
    if (methods.length === 0) return;
    const fallback = methods[0];
    if (!fallback) return;
    setDrafts((prev) => [
      ...prev,
      {
        payment_method_id: fallback.id,
        amount_cents: '0',
        reference_number: null,
        allow_change: fallback.code === 'cash',
        method_code: fallback.code,
        method_name: fallback.name,
      },
    ]);
  };

  const onChangeMethod = (index: number, method_id: string): void => {
    const method = methods.find((m) => m.id === method_id);
    if (!method) return;
    setDrafts((prev) =>
      prev.map((d, i) =>
        i === index
          ? {
              ...d,
              payment_method_id: method.id,
              method_code: method.code,
              method_name: method.name,
              allow_change: method.code === 'cash',
              reference_number: method.requires_reference ? (d.reference_number ?? '') : null,
            }
          : d,
      ),
    );
  };

  const onChangeAmountMajor = (index: number, value: string): void => {
    setDrafts((prev) =>
      prev.map((d, i) => {
        if (i !== index) return d;
        try {
          const cents = majorStringToCents(value || '0');
          return { ...d, amount_cents: cents.toString() };
        } catch {
          return d;
        }
      }),
    );
  };

  const onChangeReference = (index: number, value: string): void => {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, reference_number: value } : d)));
  };

  const onClickRemoveDraft = (index: number): void => {
    setDrafts((prev) => prev.filter((_, i) => i !== index));
  };

  const onClickCharge = async (): Promise<void> => {
    if (!final_can_submit) return;
    const positive_drafts = drafts.filter((d) => (safeBigInt(d.amount_cents) ?? 0n) > 0n);
    await on_charge(positive_drafts, { open_account_kind });
  };

  const onClickApplyDiscount = async (): Promise<void> => {
    if (!can_apply_discount || discount_input_cents === null) return;
    await on_apply_discount({
      discount_cents: discount_input_cents.toString(),
      reason: discount_reason.trim().length > 0 ? discount_reason.trim() : null,
      otp_plaintext: discount_otp.trim(),
    });
  };

  return (
    <div className="space-y-4">
      <header>
        <h3 className="text-base font-semibold">{t('cashier_pos.payment_title')}</h3>
        <p className="text-muted-foreground text-xs">{t('cashier_pos.payment_subtitle')}</p>
      </header>

      <div className="border-border bg-card/40 rounded-md border p-3">
        <div className="mt-1 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('doctor_pos.subtotal')}</span>
          <span className="font-mono">{formatCentsString(subtotal_cents)}</span>
        </div>
        <div className="mt-1 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('cashier_pos.discount_label')}</span>
          <span className="font-mono text-amber-700 dark:text-amber-300">
            -{formatCentsString(discount_target.toString())}
          </span>
        </div>
        <div className="mt-1 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('doctor_pos.tax')}</span>
          <span className="font-mono">{formatCentsString(tax_cents)}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('cashier_pos.total')}</span>
          <span className="font-mono text-lg">{formatCentsString(total_cents)}</span>
        </div>
        <div className="mt-1 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('cashier_pos.paid')}</span>
          <span className="font-mono">{formatCentsString(total_paid_cents.toString())}</span>
        </div>
        <div className="mt-1 flex items-center justify-between text-sm">
          {is_underpaid ? (
            <>
              <span className="text-destructive">{t('cashier_pos.pending')}</span>
              <span className="text-destructive font-mono">
                {formatCentsString(remaining_cents.toString())}
              </span>
            </>
          ) : null}
          {has_change ? (
            <>
              <span className="text-emerald-600 dark:text-emerald-400">
                {t('cashier_pos.change')}
              </span>
              <span className="font-mono text-emerald-600 dark:text-emerald-400">
                {formatCentsString(change_cents.toString())}
              </span>
            </>
          ) : null}
          {is_balanced ? (
            <>
              <span className="text-emerald-600 dark:text-emerald-400">
                {t('cashier_pos.balanced')}
              </span>
              <Badge variant="success">OK</Badge>
            </>
          ) : null}
          {is_overpaid_no_change ? (
            <>
              <span className="text-destructive">{t('cashier_pos.overpaid_blocked')}</span>
              <span className="text-destructive font-mono">
                {formatCentsString(remaining_cents.toString())}
              </span>
            </>
          ) : null}
          {hosp1_overpay ? (
            <>
              <span className="text-amber-700 dark:text-amber-300">
                {t('cashier_pos.credit_anticipo')}
              </span>
              <span className="font-mono text-amber-700 dark:text-amber-300">
                {formatCentsString(hosp1_credit_cents.toString())}
              </span>
            </>
          ) : null}
        </div>
      </div>

      <div className="border-border bg-card/30 rounded-md border p-3">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 text-left"
          onClick={() => setDiscountSectionOpen((open) => !open)}
          disabled={disabled || charging || applying_discount}
        >
          <div>
            <h4 className="text-sm font-semibold">{t('cashier_pos.discount_account_title')}</h4>
            <p className="text-muted-foreground text-xs">
              {t('cashier_pos.discount_account_hint')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {discount_section_open ? (
              <ChevronUp className="text-muted-foreground h-4 w-4" aria-hidden />
            ) : (
              <ChevronDown className="text-muted-foreground h-4 w-4" aria-hidden />
            )}
          </div>
        </button>

        {discount_section_open ? (
          <div className="mt-3 space-y-3">
            <div className="grid gap-2 md:grid-cols-3">
              <div className="space-y-1">
                <Label className="text-xs">{t('cashier_pos.discount_amount')}</Label>
                <Input
                  inputMode="decimal"
                  className="font-mono"
                  value={discount_amount_major}
                  onChange={(e) => setDiscountAmountMajor(e.target.value)}
                  disabled={disabled || charging || applying_discount}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('cashier_pos.discount_reason')}</Label>
                <Input
                  value={discount_reason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  placeholder={t('cashier_pos.discount_reason_placeholder')}
                  disabled={disabled || charging || applying_discount}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('cashier_pos.discount_otp')}</Label>
                <Input
                  type="password"
                  value={discount_otp}
                  onChange={(e) => setDiscountOtp(e.target.value)}
                  placeholder={t('cashier_pos.discount_otp_placeholder')}
                  disabled={disabled || charging || applying_discount}
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-muted-foreground text-[11px]">
                {t('cashier_pos.discount_current_hint', {
                  discount: formatCentsString(discount_target.toString()),
                  max: formatCentsString(max_discount_cents.toString()),
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setDiscountAmountMajor('0.00');
                    setDiscountReason('');
                    setDiscountOtp('');
                  }}
                  disabled={disabled || charging || applying_discount}
                >
                  {t('cashier_pos.discount_reset')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    void onClickApplyDiscount();
                  }}
                  disabled={!can_apply_discount}
                >
                  {applying_discount ? t('common.loading') : t('cashier_pos.apply_discount')}
                </Button>
              </div>
            </div>

            {!disabled &&
            discount_input_cents !== null &&
            discount_input_cents > max_discount_cents ? (
              <p className="text-destructive text-xs">
                {t('cashier_pos.discount_exceeds_max', {
                  max_percent: '49%',
                })}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <ul className="space-y-3">
        {drafts.map((draft, index) => {
          const method = methodFor(draft, methods);
          const requires_ref = method?.requires_reference ?? false;
          return (
            <li key={index} className="border-border bg-card/30 space-y-2 rounded-md border p-3">
              <div className="flex items-center gap-2">
                <select
                  className="border-input bg-background flex h-9 flex-1 rounded-md border px-2 text-sm"
                  value={draft.payment_method_id}
                  onChange={(e) => onChangeMethod(index, e.target.value)}
                  disabled={charging}
                >
                  {methods.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
                {drafts.length > 1 ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => onClickRemoveDraft(index)}
                    disabled={charging}
                    aria-label={t('cashier_pos.remove_payment_row')}
                  >
                    <Trash2 className="text-destructive h-4 w-4" aria-hidden />
                  </Button>
                ) : null}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">{t('cashier_pos.amount')}</Label>
                  <Input
                    inputMode="decimal"
                    className="font-mono"
                    value={centsToMajorString(draft.amount_cents)}
                    onChange={(e) => onChangeAmountMajor(index, e.target.value)}
                    disabled={charging}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    {t('cashier_pos.reference')}
                    {requires_ref ? ' *' : ''}
                  </Label>
                  <Input
                    value={draft.reference_number ?? ''}
                    onChange={(e) => onChangeReference(index, e.target.value)}
                    disabled={charging || !requires_ref}
                    placeholder={requires_ref ? t('cashier_pos.reference_placeholder') : '—'}
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onClickAddDraft}
          disabled={charging || methods.length === 0}
        >
          <Plus className="h-4 w-4" aria-hidden />
          <span>{t('cashier_pos.add_payment_row')}</span>
        </Button>
      </div>

      {missing_reference ? (
        <p className="text-destructive text-xs">{t('cashier_pos.reference_required_msg')}</p>
      ) : null}

      {open_account_available ? (
        <fieldset className="border-border bg-card/20 space-y-2 rounded-md border p-3 text-xs">
          <legend className="text-muted-foreground px-1">
            {t('cashier_pos.open_account_mode_label')}
          </legend>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="open_account_kind"
              value=""
              checked={open_account_kind === null}
              onChange={() => chooseOpenAccountKind(null)}
              disabled={charging || disabled}
              className="mt-0.5"
            />
            <span>{t('cashier_pos.open_account_mode_none')}</span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="open_account_kind"
              value="normal"
              checked={open_account_kind === 'normal'}
              onChange={() => chooseOpenAccountKind('normal')}
              disabled={charging || disabled}
              className="mt-0.5"
            />
            <span>
              {t('cashier_pos.open_account_mode_normal')}
              {open_account_kind === 'normal' && is_underpaid ? (
                <span className="ml-1 font-mono text-emerald-600 dark:text-emerald-400">
                  +{formatCentsString(remaining_cents.toString())}
                </span>
              ) : null}
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="open_account_kind"
              value="hospitalization_1"
              checked={open_account_kind === 'hospitalization_1'}
              onChange={() => chooseOpenAccountKind('hospitalization_1')}
              disabled={charging || disabled}
              className="mt-0.5"
            />
            <span>
              {t('cashier_pos.open_account_mode_hospitalization_1')}
              {open_account_kind === 'hospitalization_1' ? (
                <span className="ml-1 font-mono text-amber-700 dark:text-amber-300">
                  {formatCentsString(minimum_hospitalization_payment_cents.toString())}
                </span>
              ) : null}
            </span>
          </label>
          {open_account_kind === 'hospitalization_1' ? (
            <p className="text-muted-foreground text-[11px]">
              {t('cashier_pos.open_account_hospitalization_hint', {
                minimum: formatCentsString(minimum_hospitalization_payment_cents.toString()),
              })}
            </p>
          ) : null}
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="open_account_kind"
              value="hospitalization_2"
              checked={open_account_kind === 'hospitalization_2'}
              onChange={() => chooseOpenAccountKind('hospitalization_2')}
              disabled={charging || disabled}
              className="mt-0.5"
            />
            <span>{t('cashier_pos.open_account_mode_hospitalization_2')}</span>
          </label>
          {open_account_kind === 'hospitalization_2' ? (
            <label className="flex items-start gap-2 pl-5 text-[11px]">
              <input
                type="checkbox"
                checked={hosp2_confirmed}
                onChange={(e) => setHosp2Confirmed(e.target.checked)}
                disabled={charging || disabled}
                className="mt-0.5"
              />
              <span>{t('cashier_pos.open_account_hospitalization_2_confirm')}</span>
            </label>
          ) : null}
        </fieldset>
      ) : null}

      {disabled && disabled_reason ? (
        <p className="text-xs text-amber-700 dark:text-amber-300">{disabled_reason}</p>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={on_cancel} disabled={charging}>
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          size="lg"
          onClick={() => {
            void onClickCharge();
          }}
          disabled={!final_can_submit || charging}
        >
          <Send className="h-4 w-4" aria-hidden />
          <span>{charging ? t('common.loading') : t('cashier_pos.charge')}</span>
        </Button>
      </div>
    </div>
  );
}

function safeBigInt(s: string): bigint | null {
  try {
    if (!/^-?\d+$/.test(s)) return null;
    return BigInt(s);
  } catch {
    return null;
  }
}

function parseMoneyInput(value: string): bigint | null {
  try {
    return BigInt(majorStringToCents(value || '0'));
  } catch {
    return null;
  }
}

function methodFor(
  draft: PaymentDraftValue,
  methods: readonly PaymentMethod[],
): PaymentMethod | undefined {
  return methods.find((m) => m.id === draft.payment_method_id);
}
